import { baseApi } from "./baseApi";
import { buildPaginationQueryString } from "./queryHelpers";
import {
  PaginateQuery,
  CreateChapterRequest,
  UpdateChapterRequest,
  UpdateLessonFeatureRequest,
} from "../../interfaces";

export const chapterApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getChapter: builder.query<any, string>({
      query: (id) => ({
        url: `/api/chapter/${id}`,
      }),
      providesTags: ["Chapter"],
    }),
    getChaptersStats: builder.query<any, string>({
      query: (subjectId) => ({
        url: `/api/chapter/stats/${subjectId}/subject`,
      }),
      providesTags: ["Chapter-Stats"],
    }),
    getChaptersFlashCardsV2Stats: builder.query<any, string>({
      query: (subjectId) => ({
        url: `/api/v2/chapter/stats/${subjectId}/subject/flash-cards`,
      }),
      providesTags: ["Chapter-Flash-Card-Stats"],
    }),
    getChaptersLecturesStats: builder.query<any, string>({
      query: (subjectId) => ({
        url: `/api/chapter/stats/${subjectId}/subject/lectures`,
      }),
      providesTags: ["Chapter-Lecture-Stats"],
    }),
    getChaptersSelectListBySubject: builder.query<any, string>({
      query: (subjectId) => ({
        url: `/api/chapter/select-list/${subjectId}/subject`,
        method: "POST",
      }),
      providesTags: ["Chapter-Select-List-By-Subject"],
    }),
    getOrganizingChapters: builder.query<
      any,
      { subjectId: string; data: PaginateQuery }
    >({
      query: ({ subjectId, data }) => ({
        url: buildPaginationQueryString(
          data,
          `/api/chapter/list/${subjectId}/subject`,
        ),
      }),
      providesTags: ["Organizing-Chapters"],
    }),
    addChapter: builder.mutation<any, CreateChapterRequest>({
      query: (data) => ({
        url: "/api/chapter",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "Chapter-Stats",
        "Chapter-Flash-Card-Stats",
        "Chapter-Lecture-Stats",
        "Chapter-Select-List-By-Subject",
        "Organizing-Chapters",
      ],
    }),
    updateChapter: builder.mutation<
      any,
      { id: string; data: UpdateChapterRequest }
    >({
      query: ({ id, data }) => ({
        url: `/api/chapter/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "Chapter",
        "Chapter-Stats",
        "Chapter-Flash-Card-Stats",
        "Chapter-Lecture-Stats",
        "Chapter-Select-List-By-Subject",
        "Organizing-Chapters",
      ],
    }),
    deleteChapter: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/chapter/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "Chapter-Stats",
        "Chapter-Flash-Card-Stats",
        "Chapter-Lecture-Stats",
        "Chapter-Select-List-By-Subject",
        "Organizing-Chapters",
      ],
    }),
    toggleChapterState: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/chapter/toggle-state/${id}`,
        method: "PATCH",
      }),
      invalidatesTags: ["Organizing-Chapters", "Chapter-Stats"],
    }),
    updateLessonFeature: builder.mutation<
      any,
      { id: string; data: UpdateLessonFeatureRequest }
    >({
      query: ({ id, data }) => ({
        url: `/api/lesson/${id}/feature`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Chapter", "Organizing-Chapters"],
    }),
  }),
});

export const {
  useGetChapterQuery,
  useGetChaptersStatsQuery,
  useGetChaptersFlashCardsV2StatsQuery,
  useGetChaptersLecturesStatsQuery,
  useGetChaptersSelectListBySubjectQuery,
  useGetOrganizingChaptersQuery,
  useAddChapterMutation,
  useUpdateChapterMutation,
  useDeleteChapterMutation,
  useToggleChapterStateMutation,
  useUpdateLessonFeatureMutation,
} = chapterApi;
